import { useSafeContext } from '@aspendental/shared-utils/react';
import { createContext, useMemo, useState } from 'react';
import { BffBrandType } from '@/types/generated';
import { useAppContext } from './AppContextProvider.ctx';
import type { IUser } from '@shared-components/constants';
import useFacilities from '@shared-components/services/useFacilities';
import type { Maybe, TAGBrand } from '@shared-components/types';
export type ExtendedFacilities = ReturnType<typeof useFacilities> & {
  shouldFetchNearbyFacility: boolean;
  initialize: () => void;
};
export const FacilityContext = createContext<ExtendedFacilities>({
  officeInfo: null,
  facilityCode: undefined,
  facilityBrand: BffBrandType.Lovet,
  locationState: {
    nearByOfficeLocations: null,
    closestOfficeInfo: null,
    distance: undefined,
    schedulingSystem: null,
    user: {
      coordinates: {
        latitude: 0,
        longitude: 0
      },
      hasIpBasedCoordinates: false
    }
  },
  nearbyFacilities: undefined,
  isWaitingForNearbyFacilities: false,
  shouldFetchNearbyFacility: false,
  initialize: () => {
    return null;
  }
});
FacilityContext.displayName = 'FacilityContext';
export interface IFacilityContextProvider {
  brand: TAGBrand;
  user?: Maybe<IUser>;
  facilityCode?: string;
  children: React.ReactNode;
  defaultToCdn?: boolean;
  disableFacilityFetching?: boolean;
}
export function FacilityContextProvider({
  brand,
  user,
  facilityCode,
  children,
  defaultToCdn = false,
  disableFacilityFetching = false
}: Readonly<IFacilityContextProvider>) {
  const appConfig = useAppContext();
  const {
    config,
    environment,
    appName,
    appVersion
  } = appConfig;
  const [shouldFetchNearbyFacility, setShouldFetchNearbyFacility] = useState(!disableFacilityFetching);
  const bffGraphqlUrl = environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;
  const facilities = useFacilities({
    appName,
    appVersion,
    brand,
    bffGraphqlUrl,
    useFacilityBeffeDataSource: config.featureFlags.useFacilityBeffeDataSource,
    user,
    facilityCode,
    defaultToCdn,
    transitionallyUseBeffe: config.featureFlags.transitionallyUseBeffe,
    useSessionStorage: config.featureFlags?.useSessionStorageFacilityCode,
    shouldFetchNearbyFacility,
    showSetOfficeButtonInGoogleMapsCards: config?.featureFlags?.showSetOfficeButtonInGoogleMapsCards,
    fetchAllLocationsOnEmptyResults: config?.featureFlags?.fetchAllLocationsOnEmptyResults
  });

  // Extend facilities with lazy initialization support
  const extendedFacilities = useMemo(() => ({
    ...facilities,
    shouldFetchNearbyFacility,
    initialize: () => {
      if (!shouldFetchNearbyFacility) {
        setShouldFetchNearbyFacility(true);
      }
    }
  }), [facilities, shouldFetchNearbyFacility]);
  return <FacilityContext.Provider value={extendedFacilities} data-sentry-element="FacilityContext.Provider" data-sentry-component="FacilityContextProvider" data-sentry-source-file="FacilityContextProvider.ctx.tsx">{children}</FacilityContext.Provider>;
}
export function useFacilityContext(shouldInitialize?: boolean) {
  const context = useSafeContext(FacilityContext);
  if (shouldInitialize && !context.shouldFetchNearbyFacility) {
    setTimeout(() => context.initialize(), 0);
  }
  return context;
}